/**
 * GlobalView — Air Layer Renderer (§3.4)
 *
 * Renders aircraft entities on the CesiumJS globe as flat 3D planes.
 * Uses Cesium Entity + PlaneGraphics for true 3D flat rendering:
 * - From above: full icon visible
 * - From the side: thin line (real 3D)
 *
 * Each aircraft category (0–20) gets its own icon from aircraftIcons.ts.
 * Icons are rendered as Canvas elements and used as PlaneGraphics material.
 * Orientation is computed via headingPitchRollQuaternion for correct heading.
 *
 * Uses CallbackProperty for efficient per-frame position/orientation updates
 * without creating new property objects each frame.
 *
 * Smooth interpolation: between API updates, all aircraft visually
 * "fly" along their heading at their speed using dead-reckoning.
 * When new API data arrives, a smooth blend (lerp) transitions
 * from the current interpolated position to the new position over ~500ms.
 */

import * as Cesium from 'cesium';
import type { Entity } from '@globalview/shared';
import { useEntityStore, selectAircraft } from '../../stores/entityStore';
import { useGlobeStore } from '../../stores/globeStore';
import { connect as wsConnect, disconnect as wsDisconnect } from '../wsClient';
import { getAircraftIconCanvas } from './aircraftIcons';
import {
  startOpenSkyDirect,
  stopOpenSkyDirect,
  setViewport,
  type OpenSkyDirectStatus,
} from '../openSkyDirectClient';

// ─── Types ───────────────────────────────────────────────────────

interface AirLayerState {
  viewer: Cesium.Viewer;
  labelCollection: Cesium.LabelCollection;
  entityMap: Map<string, Cesium.Entity>;
  labelMap: Map<string, Cesium.Label>;
  unsubscribe: (() => void) | null;
  unsubscribeVisibility: (() => void) | null;
  tickRemove: Cesium.Event.RemoveCallback | null;
  cameraRemove: Cesium.Event.RemoveCallback | null;
  viewportDebounce: ReturnType<typeof setTimeout> | null;
}

/**
 * Per-entity interpolation state for dead-reckoning + blending.
 */
interface InterpolationState {
  // ─── Current base (from latest API data) ────────
  baseLat: number;
  baseLon: number;
  baseAlt: number;
  heading: number;      // degrees
  velocity: number;     // m/s
  verticalRate: number; // m/s
  entityTimestamp: number;

  // ─── Blend state ────────────────────────────────
  blendFromLat: number;
  blendFromLon: number;
  blendFromAlt: number;
  blendStartTime: number;
  isBlending: boolean;

  // ─── Last computed visual position ──────────────
  visualLat: number;
  visualLon: number;
  visualAlt: number;

  // ─── Cached Cesium objects (avoid GC allocations) ──
  visualCartesian: Cesium.Cartesian3;
  visualOrientation: Cesium.Quaternion;

  // ─── Metadata ──────────────────────────────────
  category: number;
  isOnGround: boolean;
  isSelected: boolean;
}

let state: AirLayerState | null = null;

/** Interpolation state keyed by entity id */
const interpMap = new Map<string, InterpolationState>();

// ─── Constants ───────────────────────────────────────────────────

const AIRCRAFT_FILL = '#00d4ff';
const AIRCRAFT_FILL_SELECTED = '#ffdd00';
const EARTH_RADIUS = 6_371_000;

/** Base plane size in meters (at reference distance) */
const BASE_PLANE_SIZE = 400;
const BASE_PLANE_SIZE_SELECTED = 600;

/** Min/Max plane size in meters — prevents vanishing or oversize */
const MIN_PLANE_SIZE = 200;
const MAX_PLANE_SIZE = 2000;
const MIN_PLANE_SIZE_SELECTED = 300;
const MAX_PLANE_SIZE_SELECTED = 3000;

/** Reference distance (meters) at which base size is used */
const REFERENCE_DISTANCE = 50_000;

/** Scale factor to convert distance to size */
const SIZE_SCALE_FACTOR = BASE_PLANE_SIZE / REFERENCE_DISTANCE;

/** Alpha for on-ground aircraft */
const GROUND_ALPHA = 0.35;

/** Duration of the blend over (ms) */
const BLEND_DURATION_MS = 600;

/** Max interpolation time (seconds) */
const MAX_INTERP_SECONDS = 60;

/**
 * Distance threshold (meters) for switching from 3D plane to point.
 * Below this distance: 3D plane icon visible.
 * Above this distance: simple colored point visible.
 */
const PLANE_TO_POINT_DISTANCE = 200_000;

/** Point pixel size for far-away aircraft */
const FAR_POINT_SIZE = 5;
const FAR_POINT_SIZE_SELECTED = 8;

/** Point color for far-away aircraft */
const AIRCRAFT_POINT_COLOR = Cesium.Color.fromCssColorString('#00d4ff');
const AIRCRAFT_POINT_COLOR_GROUND = Cesium.Color.fromCssColorString('#8899aa');
const AIRCRAFT_POINT_COLOR_SELECTED = Cesium.Color.fromCssColorString('#ffdd00');

// ─── Reusable scratch objects ────────────────────────────────────

const scratchHpr = new Cesium.HeadingPitchRoll();

// ─── Init ────────────────────────────────────────────────────────

export function initAirLayer(viewer: Cesium.Viewer): void {
  if (state) destroyAirLayer();

  const labelCollection = new Cesium.LabelCollection();

  const initialVisible = useGlobeStore.getState().airLayerVisible;
  const initialLabels = useGlobeStore.getState().airLabelsVisible;
  labelCollection.show = initialVisible && initialLabels;

  viewer.scene.primitives.add(labelCollection);

  state = {
    viewer,
    labelCollection,
    entityMap: new Map(),
    labelMap: new Map(),
    unsubscribe: null,
    unsubscribeVisibility: null,
    tickRemove: null,
    cameraRemove: null,
    viewportDebounce: null,
  };

  // Subscribe to entity store (rendering reacts to store changes)
  state.unsubscribe = useEntityStore.subscribe(
    (storeState) => {
      if (state) syncEntities(selectAircraft(storeState));
    },
  );

  // Per-frame interpolation
  state.tickRemove = viewer.clock.onTick.addEventListener(() => {
    if (state) interpolatePositions();
  });

  // Layer visibility — start/stop browser-direct OpenSky polling
  state.unsubscribeVisibility = useGlobeStore.subscribe(
    (globeState, prevState) => {
      if (!state) return;
      if (globeState.airLayerVisible === prevState.airLayerVisible) return;

      for (const entity of state.entityMap.values()) {
        entity.show = globeState.airLayerVisible;
      }
      state.labelCollection.show = globeState.airLayerVisible && globeState.airLabelsVisible;

      if (!globeState.airLayerVisible) {
        wsDisconnect();
        stopOpenSkyDirect();
        // Clear aircraft entities from store and maps
        useEntityStore.getState().clearByType('aircraft');
        for (const [, cesiumEntity] of state.entityMap) {
          state.viewer.entities.remove(cesiumEntity);
        }
        state.entityMap.clear();
        for (const [, label] of state.labelMap) {
          state.labelCollection.remove(label);
        }
        state.labelMap.clear();
        interpMap.clear();
        console.log('[AirLayer] Disabled');
      } else {
        wsConnect();
        startBrowserOpenSky();
        updateViewportFromCamera(viewer);
        console.log('[AirLayer] Enabled');
      }

      viewer.scene.requestRender();
    },
  );

  // Label visibility
  const unsubLabels = useGlobeStore.subscribe(
    (globeState, prevState) => {
      if (!state) return;
      if (globeState.airLabelsVisible === prevState.airLabelsVisible) return;
      state.labelCollection.show = globeState.airLayerVisible && globeState.airLabelsVisible;
      viewer.scene.requestRender();
    },
  );

  const origUnsub = state.unsubscribeVisibility;
  state.unsubscribeVisibility = () => {
    origUnsub?.();
    unsubLabels();
  };

  // Start browser-direct OpenSky polling if layer is visible
  if (initialVisible) {
    startBrowserOpenSky();
    updateViewportFromCamera(viewer);
  }

  // Camera move → update OpenSky viewport
  state.cameraRemove = viewer.camera.changed.addEventListener(() => {
    if (!state) return;
    if (state.viewportDebounce) clearTimeout(state.viewportDebounce);
    state.viewportDebounce = setTimeout(() => updateViewportFromCamera(viewer), 800);
  });

  console.log('[AirLayer] Initialized with 3D plane icons');
}

// ─── Viewport Update from Camera ─────────────────────────────────

/**
 * Computes the Cesium camera's visible bounding box and forwards it
 * to the OpenSky browser client so polls are limited to what's visible.
 * At very high altitudes (>3000 km), sets null → poll skipped entirely.
 */
function updateViewportFromCamera(viewer: Cesium.Viewer): void {
  const height = viewer.camera.positionCartographic.height;

  // Too zoomed out — global poll would return 10k+ aircraft, skip
  if (height > 3_000_000) {
    setViewport(null);
    return;
  }

  const rect = viewer.camera.computeViewRectangle(viewer.scene.globe.ellipsoid);
  if (!rect) {
    // Camera pointing at sky or void — keep last viewport
    return;
  }

  const lamin = Cesium.Math.toDegrees(rect.south);
  const lamax = Cesium.Math.toDegrees(rect.north);
  const lomin = Cesium.Math.toDegrees(rect.west);
  const lomax = Cesium.Math.toDegrees(rect.east);

  // Clamp bounding box to avoid huge viewports at medium zoom
  const latSpan = lamax - lamin;
  const lonSpan = lomax - lomin;
  if (latSpan > 50 || lonSpan > 80) {
    // Use center + fixed radius instead
    const centerLat = lamin + latSpan / 2;
    const centerLon = lomin + lonSpan / 2;
    const vp = {
      lamin: Math.max(-90, centerLat - 25),
      lamax: Math.min(90, centerLat + 25),
      lomin: Math.max(-180, centerLon - 40),
      lomax: Math.min(180, centerLon + 40),
    };
    setViewport(vp);
    cullOutsideViewport(vp);
    return;
  }

  const vp = { lamin, lomin, lamax, lomax };
  setViewport(vp);
  cullOutsideViewport(vp);
}

// ─── Viewport Culling ────────────────────────────────────────────

/** Margin (degrees) beyond viewport before culling an aircraft */
const CULL_MARGIN_DEG = 5;

/**
 * Immediately remove aircraft that are clearly outside the current viewport.
 * This prevents "ghost" aircraft on the other side of the world from
 * persisting between API polls.
 */
function cullOutsideViewport(vp: { lamin: number; lamax: number; lomin: number; lomax: number }): void {
  if (!state) return;

  const cullLamin = vp.lamin - CULL_MARGIN_DEG;
  const cullLamax = vp.lamax + CULL_MARGIN_DEG;
  const cullLomin = vp.lomin - CULL_MARGIN_DEG;
  const cullLomax = vp.lomax + CULL_MARGIN_DEG;

  const removeIds: string[] = [];

  for (const [id, interp] of interpMap) {
    const { visualLat, visualLon } = interp;
    if (visualLat < cullLamin || visualLat > cullLamax ||
        visualLon < cullLomin || visualLon > cullLomax) {
      removeIds.push(id);
    }
  }

  if (removeIds.length === 0) return;

  // Remove from Cesium rendering
  for (const id of removeIds) {
    const cesiumEntity = state.entityMap.get(id);
    if (cesiumEntity) {
      state.viewer.entities.remove(cesiumEntity);
      state.entityMap.delete(id);
    }
    const label = state.labelMap.get(id);
    if (label) {
      state.labelCollection.remove(label);
      state.labelMap.delete(id);
    }
    interpMap.delete(id);
  }

  // Remove from entity store
  useEntityStore.getState().removeEntities(removeIds);

  console.log(`[AirLayer] Culled ${removeIds.length} aircraft outside viewport`);
}

// ─── Browser-Direct OpenSky Polling ─────────────────────────────

/** Store that receives credit status updates from browser polling */
let lastOpenSkyDirectStatus: OpenSkyDirectStatus | null = null;
export function getOpenSkyBrowserStatus(): OpenSkyDirectStatus | null {
  return lastOpenSkyDirectStatus;
}

function startBrowserOpenSky(): void {
  void startOpenSkyDirect(
    (entities: Entity[]) => {
      const store = useEntityStore.getState();

      // ── Incremental diff update ──────────────────────────────────
      // Avoids full Cesium entity delete+recreate cycle on every poll.
      // 1. Find aircraft that are gone → removeEntities (triggers Cesium cleanup)
      // 2. Upsert new/updated aircraft → syncEntities updates blend state in place
      const newIdSet = new Set(entities.map((e) => e.id));
      const removedIds: string[] = [];
      for (const [id, entity] of store.entities) {
        if (entity.type === 'aircraft' && !newIdSet.has(id)) {
          removedIds.push(id);
        }
      }
      if (removedIds.length > 0) {
        store.removeEntities(removedIds);
      }
      if (entities.length > 0) {
        store.upsertEntities(entities);
      }
    },
    (status: OpenSkyDirectStatus) => {
      lastOpenSkyDirectStatus = status;
    },
  );
}

// ─── Dead Reckoning ──────────────────────────────────────────────

function deadReckon(
  lat: number, lon: number, alt: number,
  headingDeg: number, velocityMs: number, verticalRateMs: number,
  dtSeconds: number,
): { lat: number; lon: number; alt: number } {
  if (velocityMs <= 0 || dtSeconds <= 0) return { lat, lon, alt };

  const headingRad = Cesium.Math.toRadians(headingDeg);
  const distance = velocityMs * dtSeconds;

  const dLat = (distance * Math.cos(headingRad)) / EARTH_RADIUS;
  const newLat = lat + Cesium.Math.toDegrees(dLat);

  const dLon = (distance * Math.sin(headingRad)) /
    (EARTH_RADIUS * Math.cos(Cesium.Math.toRadians(lat)));
  const newLon = lon + Cesium.Math.toDegrees(dLon);

  const newAlt = Math.max(0, alt + verticalRateMs * dtSeconds);

  return { lat: newLat, lon: newLon, alt: newAlt };
}

// ─── Lerp ────────────────────────────────────────────────────────

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function easeInOut(t: number): number {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}

// ─── Orientation Helper ──────────────────────────────────────────

function computeOrientation(
  lon: number, lat: number, alt: number, headingDeg: number,
): Cesium.Quaternion {
  const position = Cesium.Cartesian3.fromDegrees(lon, lat, alt);
  scratchHpr.heading = Cesium.Math.toRadians(headingDeg);
  scratchHpr.pitch = 0;
  scratchHpr.roll = 0;
  return Cesium.Transforms.headingPitchRollQuaternion(position, scratchHpr);
}

// ─── Create PlaneGraphics Material ───────────────────────────────

function createPlaneMaterial(
  category: number, fill: string, alpha: number,
): Cesium.ImageMaterialProperty {
  return new Cesium.ImageMaterialProperty({
    image: getAircraftIconCanvas(category, fill),
    transparent: true,
    color: Cesium.Color.WHITE.withAlpha(alpha),
  });
}

// ─── Create Aircraft Entity ──────────────────────────────────────

function createAircraftEntity(
  entityId: string,
  interp: InterpolationState,
): Cesium.Entity | null {
  if (!state) return null;

  const fill = interp.isSelected ? AIRCRAFT_FILL_SELECTED : AIRCRAFT_FILL;
  const size = interp.isSelected ? BASE_PLANE_SIZE_SELECTED : BASE_PLANE_SIZE;
  const alpha = interp.isOnGround ? GROUND_ALPHA : 1.0;

  // Compute initial position and orientation
  interp.visualCartesian = Cesium.Cartesian3.fromDegrees(
    interp.visualLon, interp.visualLat, interp.visualAlt,
  );
  interp.visualOrientation = computeOrientation(
    interp.visualLon, interp.visualLat, interp.visualAlt, interp.heading,
  );

  // Use CallbackProperty to read from interpMap each frame
  // This avoids creating new Property objects per frame
  const positionCallback = new Cesium.CallbackProperty(() => {
    const i = interpMap.get(entityId);
    return i ? i.visualCartesian : Cesium.Cartesian3.ZERO;
  }, false);

  const orientationCallback = new Cesium.CallbackProperty(() => {
    const i = interpMap.get(entityId);
    return i ? i.visualOrientation : Cesium.Quaternion.IDENTITY;
  }, false);

  // Point color for far-away rendering
  const pointColor = interp.isOnGround
    ? AIRCRAFT_POINT_COLOR_GROUND.withAlpha(GROUND_ALPHA)
    : AIRCRAFT_POINT_COLOR;

  const entity = state.viewer.entities.add({
    id: entityId,
    position: positionCallback as unknown as Cesium.PositionProperty,
    orientation: orientationCallback,
    plane: {
      plane: new Cesium.Plane(Cesium.Cartesian3.UNIT_Z, 0.0),
      dimensions: new Cesium.Cartesian2(size, size),
      material: createPlaneMaterial(interp.category, fill, alpha),
      outline: false,
    },
    // Show simple point when far away
    point: {
      pixelSize: FAR_POINT_SIZE,
      color: pointColor,
      outlineColor: Cesium.Color.BLACK,
      outlineWidth: 1,
      distanceDisplayCondition: new Cesium.DistanceDisplayCondition(PLANE_TO_POINT_DISTANCE, Number.MAX_VALUE),
      disableDepthTestDistance: Number.POSITIVE_INFINITY,
      scaleByDistance: new Cesium.NearFarScalar(PLANE_TO_POINT_DISTANCE, 1.0, 2_000_000, 0.4),
    },
    show: useGlobeStore.getState().airLayerVisible,
  });

  return entity;
}

// ─── Sync Entities (on store update) ─────────────────────────────

function syncEntities(aircraft: Entity[]): void {
  if (!state) return;

  const { labelCollection, entityMap, labelMap } = state;
  const activeIds = new Set<string>();
  const now = Date.now();
  const labelsVisible = useGlobeStore.getState().airLabelsVisible;

  for (const entity of aircraft) {
    activeIds.add(entity.id);

    const props = entity.properties as Record<string, unknown>;
    const velocity = (props.velocity as number) ?? 0;
    const verticalRate = (props.verticalRate as number) ?? 0;
    const heading = entity.heading ?? 0;
    const entityTimestamp = new Date(entity.timestamp).getTime();
    const isOnGround = props.onGround === true;
    const category = (props.category as number) ?? 0;
    const callsign = (props.callsign as string | undefined);

    const existing = interpMap.get(entity.id);

    if (existing) {
      // Update blend state
      existing.blendFromLat = existing.visualLat;
      existing.blendFromLon = existing.visualLon;
      existing.blendFromAlt = existing.visualAlt;
      existing.blendStartTime = now;
      existing.isBlending = true;

      existing.baseLat = entity.position.lat;
      existing.baseLon = entity.position.lon;
      existing.baseAlt = entity.position.alt;
      existing.heading = heading;
      existing.velocity = velocity;
      existing.verticalRate = verticalRate;
      existing.entityTimestamp = entityTimestamp;

      // Update category/ground if changed
      const categoryChanged = existing.category !== category;
      const groundChanged = existing.isOnGround !== isOnGround;
      existing.category = category;
      existing.isOnGround = isOnGround;

      // Update material if category or ground state changed
      if (categoryChanged || groundChanged) {
        const cesiumEntity = entityMap.get(entity.id);
        if (cesiumEntity?.plane) {
          const fill = existing.isSelected ? AIRCRAFT_FILL_SELECTED : AIRCRAFT_FILL;
          const alpha = isOnGround ? GROUND_ALPHA : 1.0;
          cesiumEntity.plane.material = createPlaneMaterial(category, fill, alpha);
        }
      }
    } else {
      // New entity
      const initial: InterpolationState = {
        baseLat: entity.position.lat,
        baseLon: entity.position.lon,
        baseAlt: entity.position.alt,
        heading,
        velocity,
        verticalRate,
        entityTimestamp,
        blendFromLat: entity.position.lat,
        blendFromLon: entity.position.lon,
        blendFromAlt: entity.position.alt,
        blendStartTime: 0,
        isBlending: false,
        visualLat: entity.position.lat,
        visualLon: entity.position.lon,
        visualAlt: entity.position.alt,
        visualCartesian: Cesium.Cartesian3.fromDegrees(
          entity.position.lon, entity.position.lat, entity.position.alt,
        ),
        visualOrientation: computeOrientation(
          entity.position.lon, entity.position.lat, entity.position.alt, heading,
        ),
        category,
        isOnGround,
        isSelected: false,
      };
      interpMap.set(entity.id, initial);

      const cesiumEntity = createAircraftEntity(entity.id, initial);
      if (cesiumEntity) {
        entityMap.set(entity.id, cesiumEntity);
      }
    }

    // Labels
    if (callsign) {
      const existingLabel = labelMap.get(entity.id);
      if (!existingLabel) {
        const position = Cesium.Cartesian3.fromDegrees(
          entity.position.lon, entity.position.lat, entity.position.alt,
        );
        const label = labelCollection.add({
          position,
          text: callsign,
          font: '12px Inter, Helvetica, sans-serif',
          fillColor: Cesium.Color.fromCssColorString('#e0f7ff'),
          outlineColor: Cesium.Color.BLACK,
          outlineWidth: 2,
          style: Cesium.LabelStyle.FILL_AND_OUTLINE,
          pixelOffset: new Cesium.Cartesian2(16, -4),
          show: labelsVisible,
          id: entity.id,
          scaleByDistance: new Cesium.NearFarScalar(1e4, 1.2, 5e5, 0.0),
          distanceDisplayCondition: new Cesium.DistanceDisplayCondition(0, 500_000),
        });
        labelMap.set(entity.id, label);
      }
    }
  }

  // Remove entities no longer present
  for (const [id, cesiumEntity] of entityMap) {
    if (!activeIds.has(id)) {
      state.viewer.entities.remove(cesiumEntity);
      entityMap.delete(id);
      interpMap.delete(id);
    }
  }

  for (const [id, label] of labelMap) {
    if (!activeIds.has(id)) {
      labelCollection.remove(label);
      labelMap.delete(id);
    }
  }
}

// ─── Per-Frame Interpolation ─────────────────────────────────────

/**
 * Called each frame via clock onTick.
 * Updates the cached Cartesian3 and Quaternion in interpMap.
 * CallbackProperty on each entity reads these each frame.
 */
function interpolatePositions(): void {
  if (!state) return;

  const now = Date.now();
  const { labelMap } = state;

  for (const [id, interp] of interpMap) {
    let lat: number, lon: number, alt: number;

    if (interp.isBlending) {
      const elapsed = now - interp.blendStartTime;
      const t = Math.min(elapsed / BLEND_DURATION_MS, 1.0);
      const eased = easeInOut(t);

      const dtFromBase = (now - interp.entityTimestamp) / 1000;
      const target = deadReckon(
        interp.baseLat, interp.baseLon, interp.baseAlt,
        interp.heading, interp.velocity, interp.verticalRate,
        Math.min(dtFromBase, MAX_INTERP_SECONDS),
      );

      lat = lerp(interp.blendFromLat, target.lat, eased);
      lon = lerp(interp.blendFromLon, target.lon, eased);
      alt = lerp(interp.blendFromAlt, target.alt, eased);

      if (t >= 1.0) {
        interp.isBlending = false;
      }
    } else {
      const dtSeconds = (now - interp.entityTimestamp) / 1000;
      if (dtSeconds <= 0 || dtSeconds > MAX_INTERP_SECONDS || interp.velocity <= 0) {
        lat = interp.baseLat;
        lon = interp.baseLon;
        alt = interp.baseAlt;
      } else {
        const dr = deadReckon(
          interp.baseLat, interp.baseLon, interp.baseAlt,
          interp.heading, interp.velocity, interp.verticalRate,
          dtSeconds,
        );
        lat = dr.lat;
        lon = dr.lon;
        alt = dr.alt;
      }
    }

    // Store visual position
    interp.visualLat = lat;
    interp.visualLon = lon;
    interp.visualAlt = alt;

    // Update cached Cartesian3 and Quaternion (read by CallbackProperty)
    interp.visualCartesian = Cesium.Cartesian3.fromDegrees(lon, lat, alt);
    interp.visualOrientation = computeOrientation(lon, lat, alt, interp.heading);

    // Dynamic plane sizing + LOD: hide 3D plane beyond threshold distance
    const cesiumEntity = state.entityMap.get(id);
    if (cesiumEntity?.plane) {
      const cameraPos = state.viewer.camera.positionWC;
      const dist = Cesium.Cartesian3.distance(cameraPos, interp.visualCartesian);

      // LOD switch: beyond threshold, hide expensive 3D plane → point takes over
      if (dist > PLANE_TO_POINT_DISTANCE) {
        cesiumEntity.plane.show = new Cesium.ConstantProperty(false);
      } else {
        cesiumEntity.plane.show = new Cesium.ConstantProperty(true);
        const rawSize = dist * SIZE_SCALE_FACTOR;

        if (interp.isSelected) {
          const size = Math.max(MIN_PLANE_SIZE_SELECTED, Math.min(MAX_PLANE_SIZE_SELECTED, rawSize * 1.5));
          cesiumEntity.plane.dimensions = new Cesium.ConstantProperty(
            new Cesium.Cartesian2(size, size),
          );
        } else {
          const size = Math.max(MIN_PLANE_SIZE, Math.min(MAX_PLANE_SIZE, rawSize));
          cesiumEntity.plane.dimensions = new Cesium.ConstantProperty(
            new Cesium.Cartesian2(size, size),
          );
        }
      }
    }

    // Update label position
    const label = labelMap.get(id);
    if (label) {
      label.position = interp.visualCartesian;
    }
  }
}

// ─── Highlight Selected Entity ───────────────────────────────────

export function highlightAirEntity(entityId: string | null): void {
  if (!state) return;

  for (const [id, cesiumEntity] of state.entityMap) {
    const interp = interpMap.get(id);
    if (!interp) continue;

    if (id === entityId) {
      interp.isSelected = true;
      if (cesiumEntity.plane) {
        cesiumEntity.plane.material = createPlaneMaterial(
          interp.category, AIRCRAFT_FILL_SELECTED, 1.0,
        );
      }
      if (cesiumEntity.point) {
        cesiumEntity.point.color = new Cesium.ConstantProperty(AIRCRAFT_POINT_COLOR_SELECTED);
        cesiumEntity.point.pixelSize = new Cesium.ConstantProperty(FAR_POINT_SIZE_SELECTED);
      }
    } else if (interp.isSelected) {
      interp.isSelected = false;
      const alpha = interp.isOnGround ? GROUND_ALPHA : 1.0;
      if (cesiumEntity.plane) {
        cesiumEntity.plane.material = createPlaneMaterial(
          interp.category, AIRCRAFT_FILL, alpha,
        );
      }
      if (cesiumEntity.point) {
        const pointColor = interp.isOnGround
          ? AIRCRAFT_POINT_COLOR_GROUND.withAlpha(GROUND_ALPHA)
          : AIRCRAFT_POINT_COLOR;
        cesiumEntity.point.color = new Cesium.ConstantProperty(pointColor);
        cesiumEntity.point.pixelSize = new Cesium.ConstantProperty(FAR_POINT_SIZE);
      }
    }
  }
}

// ─── Get interpolated position for an entity ─────────────────────

export function getInterpolatedPosition(entityId: string): { lat: number; lon: number; alt: number } | null {
  const interp = interpMap.get(entityId);
  if (!interp) return null;
  return { lat: interp.visualLat, lon: interp.visualLon, alt: interp.visualAlt };
}

// ─── Destroy ─────────────────────────────────────────────────────

export function destroyAirLayer(): void {
  if (!state) return;

  if (state.unsubscribe) state.unsubscribe();
  if (state.unsubscribeVisibility) state.unsubscribeVisibility();
  if (state.tickRemove) state.tickRemove();
  if (state.cameraRemove) state.cameraRemove();
  if (state.viewportDebounce) clearTimeout(state.viewportDebounce);

  stopOpenSkyDirect();

  if (!state.viewer.isDestroyed()) {
    for (const entity of state.entityMap.values()) {
      state.viewer.entities.remove(entity);
    }
    state.viewer.scene.primitives.remove(state.labelCollection);
  }

  state.entityMap.clear();
  state.labelMap.clear();
  interpMap.clear();
  state = null;

  console.log('[AirLayer] Destroyed');
}

/** Check if air layer is initialized */
export function isAirLayerActive(): boolean {
  return state !== null;
}
